import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Collapse,
  Divider,
  Row,
  Statistic,
  Tag,
  Typography,
  Spin,
} from "antd";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import { apiConfig, labelConfig, staticConfig } from "./securityProduct.config";
import { BOM } from "../../../services/definedMethod";
import cnt from "../../../services/definedConstant";

const { Panel } = Collapse;
const { Text, Title } = Typography;

const ViewSecurityProduct = ({ userData }) => {
  const { session } = userData;
  const { state: securityProductId } = useLocation();

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);

  /* ===================== HELPERS ===================== */

  const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "-");

  const renderTag = (value) => (
    <Tag
      style={{
        background: "#FDF4FF",
        border: "1px solid #F6D0FE",
        borderRadius: 16,
      }}
    >
      <Text style={{ color: "#9F1AB1", fontWeight: 600 }}>{value ?? "-"}</Text>
    </Tag>
  );

  const renderGL = (gl) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {gl.debit && (
        <Tag
          style={{
            background: "#FFF7ED",
            border: "1px solid #FED7AA",
            borderRadius: 12,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          <Text strong>Debit:</Text>{" "}
          <Text>
            {gl.debit.name} ({gl.debit.glCode})
          </Text>
        </Tag>
      )}

      {gl.credit && (
        <Tag
          style={{
            background: "#F0FDF4",
            border: "1px solid #BBF7D0",
            borderRadius: 12,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          <Text strong>Credit:</Text>{" "}
          <Text>
            {gl.credit.name} ({gl.credit.glCode})
          </Text>
        </Tag>
      )}
    </div>
  );

  const resolveDisplayValue = (val) => {
    if (val === null || val === undefined || val === "") return "-";

    // GL-like object → structured
    if (typeof val === "object" && (val.debitAccount || val.creditAccount)) {
      return {
        __type: "GL",
        debit: val.debitAccount,
        credit: val.creditAccount,
      };
    }

    // Array
    if (Array.isArray(val)) {
      if (val.length === 0) return "-";
      return val.map((v) =>
        typeof v === "object" ? (v.text ?? v.value ?? "-") : v,
      );
    }

    // Normal object
    if (typeof val === "object") {
      return val.text ?? val.value ?? "-";
    }

    return val;
  };

  const renderRow = (item, index, total) => {
    const isLast = index === total - 1;
    const value = resolveDisplayValue(item.value);

    const isGL =
      typeof value === "object" && value !== null && value.__type === "GL";

    return (
      <div
        key={item.name}
        style={{
          display: "flex",
          borderBottom: isLast ? "none" : "1px solid #f0f0f0",
        }}
      >
        {/* LABEL */}
        <div
          style={{
            padding: "16px 24px",
            width: "50%",
            backgroundColor: "#f9f9f9",
            fontWeight: 500,
            borderRight: "1px solid #f0f0f0",
          }}
        >
          {item.name}
        </div>

        {/* VALUE */}
        <div style={{ padding: "16px 24px", width: "50%" }}>
          {/* GL (Debit / Credit) */}
          {isGL ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {value.debit && (
                <Tag
                  style={{
                    background: "#FFF7ED",
                    border: "1px solid #FED7AA",
                    borderRadius: 12,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  <Text strong>Debit:</Text>{" "}
                  <Text>
                    {value.debit.name} ({value.debit.glCode})
                  </Text>
                </Tag>
              )}

              {value.credit && (
                <Tag
                  style={{
                    background: "#F0FDF4",
                    border: "1px solid #BBF7D0",
                    borderRadius: 12,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  <Text strong>Credit:</Text>{" "}
                  <Text>
                    {value.credit.name} ({value.credit.glCode})
                  </Text>
                </Tag>
              )}
            </div>
          ) : Array.isArray(value) ? (
            /* ARRAY → MULTIPLE TAGS */
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {value.map((v, i) => (
                <Tag
                  key={i}
                  style={{
                    background: "#FDF4FF",
                    border: "1px solid #F6D0FE",
                    borderRadius: 16,
                  }}
                >
                  <Text style={{ color: "#9F1AB1", fontWeight: 600 }}>{v}</Text>
                </Tag>
              ))}
            </div>
          ) : item.isTag ? (
            renderTag(value)
          ) : (
            <Text
              style={{
                fontWeight: 600,
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              {value}
            </Text>
          )}
        </div>
      </div>
    );
  };

  /* ===================== API ===================== */

  const getSecurityProductById = (id) => {
    setLoading(true);
    const url = `${apiConfig.GetSecurityProducts}/${id}`;
    const body = { session };

    BOM.FetchReqAction(body, url, (err, res) => {
      setLoading(false);

      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
        return;
      }
      setDetails(res);
    });
  };

  useEffect(() => {
    if (securityProductId) getSecurityProductById(securityProductId);
  }, [securityProductId]);

  if (!details) return null;

  /* ===================== SECTIONS ===================== */

  const sections = [
  // 1. Product Identification & Classification
  {
    section: "Product Identification & Classification",
    items: [
      { name: "Product Name", value: details.productName },
      { name: "Product Code", value: details.productCode, isTag: true },
      { name: "Product Category", value: details.productCategory?.text, isTag: true },
      { name: "Instrument Type", value: details.instrumentType?.text, isTag: true },
      { name: "Asset Class", value: details.assetClass?.text, isTag: true },
      { name: "Issuer Type", value: details.issuerType?.text, isTag: true },
      { name: "Currency", value: details.currencyCode, isTag: true },
      { name: "Tradability", value: yesNo(details.tradabilityFlag), isTag: true },
      { name: "Status", value: details.status?.text, isTag: true },
    ],
  },

  // 2. Economic Behaviour & Cashflow Rules
  {
    section: "Economic Behaviour & Cashflow Rules",
    items: [
      { name: "Interest Structure", value: details.interestStructure, isTag: true },
      { name: "Coupon Type", value: details.couponType, isTag: true },
      { name: "Coupon Frequency", value: details.couponFrequency, isTag: true },
      { name: "Amortisation Type", value: details.amortisationType, isTag: true },
      { name: "Principal Repayment Type", value: details.principalRepaymentType, isTag: true },
      { name: "Income Recognition Method", value: details.incomeRecognitionMethod, isTag: true },
      { name: "Accrual Required", value: yesNo(details.accrualRequiredFlag), isTag: true },
      { name: "Accrual Frequency", value: details.accrualFrequency, isTag: true },
    ],
  },

  // 3. Pricing & Yield Configuration
  {
    section: "Pricing & Yield Configuration",
    items: [
      { name: "Pricing Method", value: details.pricingMethod, isTag: true },
      { name: "Quotation Type", value: details.quotationType, isTag: true },
      { name: "Yield Basis", value: details.yieldBasis, isTag: true },
      { name: "Discount Basis", value: details.discountBasis, isTag: true },
      { name: "Price Decimals", value: details.priceDecimals },
      { name: "Yield Decimals", value: details.yieldDecimals },
      { name: "Clean / Dirty Price", value: details.cleanDirtyPriceFlag === "DIRTY" ? "Dirty" : "Clean", isTag: true },
      { name: "Valuation Curve Type", value: details.valuationCurveType, isTag: true },
    ],
  },

  // 4. Day Count & Calendar Rules
  {
    section: "Day Count & Calendar Rules",
    items: [
      { name: "Day Count Convention", value: details.dayCountConvention, isTag: true },
      { name: "Business Day Convention", value: details.businessDayConvention, isTag: true },
      { name: "Holiday Calendar", value: details.holidayCalendarCode, isTag: true },
      { name: "Settlement Days", value: details.settlementDays },
      { name: "Maturity Adjustment Rule", value: yesNo(details.maturityAdjustmentRule), isTag: true },
    ],
  },

  // 5. Accounting & IFRS Behaviour
  {
    section: "Accounting & IFRS Behaviour",
    items: [
      { name: "IFRS Classification", value: details.ifrsClassification, isTag: true },
      { name: "Accounting Basis", value: details.accountingBasis, isTag: true },
      { name: "Revaluation Required", value: yesNo(details.revaluationRequiredFlag), isTag: true },
      { name: "Revaluation Frequency", value: details.revaluationFrequency, isTag: true },
      { name: "Principal GL", value: details.principalGL, isTag: true },
      { name: "Accrual GL", value: details.accruedInterestGL, isTag: true },
      { name: "Interest Income GL", value: details.interestIncomeGL, isTag: true },
      { name: "Unrealised Income GL", value: details.unrealisedIncomeGL, isTag: true },
    ],
  },

  // 6. Risk & Regulatory Attributes
  {
    section: "Risk & Regulatory Attributes",
    items: [
      { name: "Risk Weight (%)", value: details.riskWeight },
      { name: "Duration Method", value: details.durationMethod?.text, isTag: true },
      { name: "Exposure Type", value: details.exposureType?.text, isTag: true },
      { name: "Eligible for SLR", value: yesNo(details.eligibleForSlrFlag), isTag: true },
      { name: "Eligible for LCR", value: yesNo(details.eligibleForLcrFlag), isTag: true },
      { name: "Haircut Percentage (%)", value: details.haircutPercentage },
      { name: "Credit Risk Class", value: details.creditRiskClass?.text, isTag: true },
    ],
  },

  // 7. Lifecycle & Event Controls
  {
    section: "Lifecycle & Event Controls",
    items: [
      { name: "Early Sale Allowed", value: yesNo(details.earlySaleAllowed), isTag: true },
      { name: "Rollover Allowed", value: yesNo(details.rolloverAllowed), isTag: true },
      { name: "Auto Maturity", value: yesNo(details.autoMaturityFlag), isTag: true },
      { name: "Maturity Event Action", value: details.maturityEventCode?.text, isTag: true },
      { name: "Penalty Applicable", value: yesNo(details.penaltyApplicableFlag), isTag: true },
    ],
  },

  // 8. Limits & Eligibility Rules
  {
    section: "Limits & Eligibility Rules",
    items: [
      { name: "Counterparty Limit Required", value: yesNo(details.counterpartyLimitRequired), isTag: true },
      { name: "Portfolio Limit Required", value: yesNo(details.portfolioLimitRequired), isTag: true },
      { name: "Minimum Deal Size", value: details.dealSizeMin?.toLocaleString() },
      { name: "Maximum Deal Size", value: details.dealSizeMax?.toLocaleString() },
      { name: "Currency Restriction", value: Array.isArray(details.currencyRestriction) ? details.currencyRestriction.map(c => c.text ?? c).join(", ") : details.currencyRestriction, isTag: true },
    ],
  },
];


  /* ===================== UI ===================== */

  return (
    <MainLayout pageItems={staticConfig.viewSecurityProduct}>
      <TopContent
        pageTitle={labelConfig.securityProduct}
        createPageTitle={labelConfig.viewSecurityProduct}
      />

      <Card
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
          marginTop: 20,
        }}
      >
        <Spin spinning={loading}>
          <div style={{ paddingTop: 20, marginBottom: 50 }}>
            <Title level={4}>{labelConfig.generalInfo}</Title>
            <Divider />

            <Row>
              <Col span={12} />
              <Col span={12}>
                <Card hoverable style={{ backgroundColor: "#EAE7EC" }}>
                  <Statistic title="Product Name" value={details.productName} />
                  <Statistic title="Product Code" value={details.productCode} />
                </Card>
              </Col>
            </Row>

            <Row
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 20,
                marginTop: 20,
              }}
            >
              {sections.map((section) => (
                <Col key={section.section}>
                  <Collapse expandIcon={<ChevronDown />}>
                    <Panel header={section.section}>
                      <div
                        style={{
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          overflow: "hidden",
                        }}
                      >
                        {section.items.map((item, i) =>
                          renderRow(item, i, section.items.length),
                        )}
                      </div>
                    </Panel>
                  </Collapse>
                </Col>
              ))}
            </Row>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps)(ViewSecurityProduct);
